// Shell presentacional do aceite da Política de Não-Retaliação (LGP-02).
//
// Extraído das 3 cópias por-SPA (web-admin / web-comite / web-recurso) em F3-B.
// É PURO de infra: não importa i18n nem auth-client. Recebe labels (via i18n
// local de cada app), versão (LGP_02_VERSION de @aions/core) e callbacks. O
// corpo da política (idêntico nas 3 cópias) vive aqui — bump editorial = editar
// <PolicyContent/> + bump de LGP_02_VERSION em core/auth/policy.ts no mesmo PR.
//
// Tematização: ver PolicyAcceptanceModal.css — usa tokens crus reais, corrigindo
// o bug dark-mode (texto quase-preto sobre navy) que existia nas cópias antigas.

import { useEffect, useRef, useState } from 'react';
import './PolicyAcceptanceModal.css';

export interface PolicyModalLabels {
  title: string;
  subtitle: string;
  versionLabel: string;
  draftWarning: string;
  scrollHint: string;
  checkbox: string;
  acceptButton: string;
  accepting: string;
  logoutButton: string;
  errorNetwork: string;
  errorSubmit: string;
}

/** Resultado do aceite no backend, normalizado pelo app. */
export type PolicyAcceptResult = { ok: true } | { ok: false; error: 'network' | 'submit' };

export interface PolicyAcceptanceModalProps {
  /** Versão vigente da política (LGP_02_VERSION). */
  version: string;
  /** Textos localizados — cada app passa via seu i18n. */
  labels: PolicyModalLabels;
  /** Registra o aceite no backend. O shell gerencia submitting/erro. */
  onAccept: () => Promise<PolicyAcceptResult>;
  /** Chamado após aceite bem-sucedido (app re-sincroniza a sessão). */
  onAccepted: () => void;
  /** Sair sem aceitar (app faz logout + navegação). */
  onLogout: () => void | Promise<void>;
}

export function PolicyAcceptanceModal({
  version,
  labels,
  onAccept,
  onAccepted,
  onLogout,
}: PolicyAcceptanceModalProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Habilita o aceite quando o conteúdo foi totalmente rolado OU quando cabe na
  // tela (monitor grande) — não bloquear quem já leu tudo.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = (): void => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
      const noScrollNeeded = el.scrollHeight <= el.clientHeight + 4;
      if (atBottom || noScrollNeeded) setScrolledToBottom(true);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => {
      el.removeEventListener('scroll', check);
    };
  }, []);

  const onSubmit = async (): Promise<void> => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError(null);
    const res = await onAccept();
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error === 'network' ? labels.errorNetwork : labels.errorSubmit);
      return;
    }
    onAccepted();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-modal-title"
      className="cf-policy-backdrop"
    >
      <div className="cf-policy-dialog">
        <header className="cf-policy-header">
          <h2 id="policy-modal-title" className="cf-policy-title">
            {labels.title}
          </h2>
          <p className="cf-policy-subtitle">
            {labels.subtitle} · {labels.versionLabel}: {version}
          </p>
        </header>

        <div ref={scrollRef} className="cf-policy-body">
          <div role="alert" className="cf-policy-draft">
            {labels.draftWarning}
          </div>

          <PolicyContent />
        </div>

        <footer className="cf-policy-footer">
          {!scrolledToBottom && <p className="cf-policy-scroll-hint">{labels.scrollHint}</p>}
          <label
            className={`cf-policy-checkbox ${
              scrolledToBottom ? 'cf-policy-checkbox--enabled' : 'cf-policy-checkbox--disabled'
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={!scrolledToBottom}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>{labels.checkbox}</span>
          </label>
          {error && (
            <p role="alert" className="cf-policy-error">
              {error}
            </p>
          )}
          <div className="cf-policy-actions">
            <button
              type="button"
              onClick={() => void onLogout()}
              disabled={submitting}
              className="cf-policy-btn cf-policy-btn--ghost"
            >
              {labels.logoutButton}
            </button>
            <button
              type="button"
              onClick={() => void onSubmit()}
              disabled={!checked || submitting}
              className="cf-policy-btn cf-policy-btn--primary"
            >
              {submitting ? labels.accepting : labels.acceptButton}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Conteúdo estruturado da LGP-02 v0.1. Fonte única (antes triplicado nas 3 SPAs).
 * Bump editorial = atualizar este componente + bump de LGP_02_VERSION em
 * core/auth/policy.ts no mesmo PR (re-aceite automático via middleware quando
 * users.policy_version != LGP_02_VERSION).
 */
function PolicyContent(): JSX.Element {
  return (
    <article className="cf-policy-content">
      <section>
        <h3>1. Propósito</h3>
        <p>
          Esta política garante que qualquer pessoa que utilize o{' '}
          <strong>Canal de Denúncias da Contato Facilites</strong> de boa-fé esteja protegida contra
          qualquer forma de represália, direta ou indireta, decorrente do ato de denunciar. A
          proteção alcança tanto o denunciante quanto testemunhas, colaboradores que prestem auxílio
          à apuração e membros do comitê de avaliação.
        </p>
      </section>

      <section>
        <h3>2. Fundamentação legal</h3>
        <ul>
          <li>Lei nº 14.457/2022, art. 23 — programa de integridade e canal de denúncias.</li>
          <li>Lei nº 12.846/2013 (Lei Anticorrupção), art. 7º, inciso VIII.</li>
          <li>NR-01, item 1.5.3.1.2 — gestão de riscos psicossociais.</li>
          <li>LGPD — Lei nº 13.709/2018, arts. 6º e 7º.</li>
          <li>Convenção nº 190 da OIT (ratificada em 2022).</li>
          <li>CLT, art. 482 e seguintes — limites do poder disciplinar.</li>
        </ul>
      </section>

      <section>
        <h3>3. Garantias ao denunciante de boa-fé</h3>
        <p>A Contato Facilites assegura, sem condicionantes, que o denunciante de boa-fé:</p>
        <ol>
          <li>
            Não sofrerá demissão, transferência punitiva, rebaixamento, alteração unilateral de
            jornada, redução salarial ou retirada de benefícios em razão da denúncia.
          </li>
          <li>
            Não será objeto de avaliação de desempenho negativa, exclusão de processos seletivos
            internos, bloqueio de promoções ou capacitações por motivo relacionado à denúncia.
          </li>
          <li>
            Não terá sua identidade revelada a terceiros não autorizados. O sistema técnico não
            armazena dados que permitam identificação (IP, dispositivo, geolocalização, navegador).
          </li>
          <li>
            Não sofrerá ostracismo, isolamento social, microagressões, mudança injustificada de
            gestor imediato ou qualquer prática informal que configure ambiente hostil.
          </li>
          <li>
            Terá acesso a apoio psicológico custeado pela empresa, mediante solicitação ao SESMT.
          </li>
          <li>Manterá direito de recurso em todas as fases do procedimento.</li>
        </ol>
      </section>

      <section>
        <h3>4. Garantias a testemunhas, auxiliares e membros do comitê</h3>
        <p>
          As mesmas garantias do item 3 estendem-se a testemunhas convocadas para oitiva,
          colaboradores que prestem informações ou documentos no curso da apuração, e membros do
          comitê de avaliação no exercício de suas funções.
        </p>
      </section>

      <section>
        <h3>5. Vedações expressas</h3>
        <p>É expressamente vedado a qualquer gestor, par hierárquico, fornecedor ou cliente:</p>
        <ul>
          <li>Indagar à pessoa se ela apresentou denúncia.</li>
          <li>Pressionar, coagir ou induzir alguém a retirar denúncia.</li>
          <li>Comentar publicamente o conteúdo de denúncia em apuração.</li>
          <li>
            Adotar qualquer das condutas listadas no item 3 em desfavor de pessoa identificada como
            denunciante, testemunha ou auxiliar.
          </li>
        </ul>
        <p>
          A violação deste item constitui <strong>falta grave</strong>, passível das medidas
          previstas no item 6.
        </p>
      </section>

      <section>
        <h3>6. Sanções por retaliação</h3>
        <p>Comprovada a retaliação, mediante apuração pelo comitê, aplicam-se cumulativamente:</p>
        <ul>
          <li>
            <strong>Leve</strong> (ato isolado, sem dano material): advertência formal escrita +
            reintegração.
          </li>
          <li>
            <strong>Média</strong> (padrão repetido ou dano funcional): suspensão de 1 a 30 dias +
            reintegração + retratação formal.
          </li>
          <li>
            <strong>Grave</strong> (demissão, prejuízo material ou moral severo): demissão por justa
            causa do retaliador (art. 482, CLT) + reintegração do prejudicado + indenização
            administrativa + comunicação ao Ministério Público do Trabalho.
          </li>
        </ul>
        <p>
          Para fornecedores, clientes e terceiros, a retaliação enseja rescisão imediata do contrato
          comercial, sem prejuízo de medidas judiciais cabíveis.
        </p>
      </section>

      <section>
        <h3>7. Proteção em caso de denúncia infundada</h3>
        <p>
          A não confirmação dos fatos denunciados, por si só, <strong>não caracteriza má-fé</strong>
          . O denunciante de boa-fé permanece integralmente protegido por esta política, mesmo
          quando a apuração concluir pela improcedência da denúncia.
        </p>
        <p>A presunção é sempre em favor da boa-fé.</p>
      </section>

      <section>
        <h3>8. Confidencialidade do processo de apuração</h3>
        <p>
          Todos os membros do comitê — Jéssica Frazão (SESMT), Fabiana Carvalho (RH) e Vinícius
          Paulino do Nascimento (Compras) — assumem dever formal de confidencialidade. A quebra de
          confidencialidade por membro do comitê constitui falta grave e enseja, no mínimo,
          afastamento imediato do colegiado e apuração disciplinar.
        </p>
        <p>
          O sistema técnico registra todas as ações dos membros do comitê em log criptograficamente
          encadeado, imutável e auditável.
        </p>
      </section>

      <section>
        <h3>9. Vigência e revisão</h3>
        <p>
          Esta política entra em vigor na data de aprovação formal pela Diretoria e será revisada
          anualmente ou sempre que houver alteração legislativa relevante. Toda pessoa com acesso ao
          Canal de Denúncias deve aceitá-la formalmente antes do primeiro uso.
        </p>
      </section>

      <p className="cf-policy-footnote">
        Versão completa em <code>docs/policy/LGP-02-politica-nao-retaliacao.md</code>. Em caso de
        divergência entre esta exibição e o documento oficial assinado pela Diretoria, prevalece o
        documento assinado.
      </p>
    </article>
  );
}
